import { useState } from 'react';
import { Link } from 'react-router-dom';
import { familyAlertChannels, familyRequest, type ApiError, type FamilyAlertChannelsResponse, type FamilyMomentsResponse } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';

/**
 * The Family Portal home's V8/V9 rows under the day note (design "Family summary" and
 * "Family activity and check-in"):
 *
 *  - Family summary: the care team's summary (GET /family/:rid/portal) and Message the team.
 *    The design's Bills button has no billing route yet, so it is not drawn.
 *  - Today with her: the design's feed of what the team logged today. No route serves those
 *    entries yet, so the card says it is not connected and shows nothing invented.
 *  - Quick check-in: one question a week, sent as a routed request (POST /family/:rid/requests,
 *    kind checkin | checkin:callback), as the phone does (FamilyCheckIn.tsx).
 *  - How you hear from us: how this person is reached (GET /family/:rid/alert-channels, changed
 *    in Settings › My connections, where the text-message consent is asked), the Sunday email
 *    (PUT /family/weekly-email) and whether she agreed to activity photos (moments.photoConsent).
 */

const CARD = 'display:flex;flex-direction:column;gap:8px;padding:12px 14px;border-radius:12px;border:1px solid rgba(34,39,46,.12);background:#FCFAF4;min-width:0;';
const LABEL = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;';
const seg = (on: boolean) =>
  'display:inline-flex;align-items:center;min-height:44px;padding:0 14px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;' +
  (on ? 'color:#FCFAF4;background:#2F5C97;' : 'color:#22272E;border:1px solid rgba(34,39,46,.2);');

export const CHECKIN_OPTIONS = ['Very good', 'Good', 'Okay', 'Not good', 'Bad'] as const;
export type CheckInAnswer = (typeof CHECKIN_OPTIONS)[number];
export const wantsCallBack = (a: CheckInAnswer) => a === 'Not good' || a === 'Bad';
export const checkInThanks = (a: CheckInAnswer) =>
  wantsCallBack(a) ? `Thank you. You said “${a}”. Your answer went to the care team as a request to call you back.` : 'Thank you. Your answer went to the care team.';

/** "Text and email": the channels this person is reached on, in plain words. */
export function channelWords(c: FamilyAlertChannelsResponse | null | undefined): string {
  if (!c) return 'Not connected yet';
  if (!c.choice.chosen || !c.choice.channels.length) return 'Not chosen yet';
  const W: Record<string, string> = { push: 'App notification', sms: 'Text', whatsapp: 'WhatsApp', email: 'Email' };
  const w = c.choice.channels.map((x) => W[x] ?? x);
  const list = w.length === 1 ? w[0] : `${w.slice(0, -1).join(', ')} and ${w[w.length - 1].toLowerCase()}`;
  return list.charAt(0).toUpperCase() + list.slice(1);
}

export function photoWords(consent: boolean | null | undefined, first: string): string {
  if (consent === true) return `On. Only photos staff take at group activities. ${first} agreed and can change this any time.`;
  if (consent === false) return `Off. ${first} has not agreed to activity photos.`;
  return `Not asked yet. ${first} decides whether activity photos are shared.`;
}

export function FpSummaryRow({ summary, onMessage }: { summary: string; onMessage: () => void }) {
  return (
    <div data-screen-label="Family summary" data-testid="fp-summary-row" style={sx('display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;padding:10px 14px;margin:4px 0 10px;border-radius:12px;border:1px solid rgba(34,39,46,.12);background:#FCFAF4;')}>
      <span style={sx('flex:1 1 360px;min-width:0;font-size:17px;font-weight:600;color:#22272E;line-height:1.45;text-wrap:pretty;')}>{summary || 'The care team has not written a summary yet.'}</span>
      <a
        href="#"
        data-testid="fp-message-team"
        onClick={(e) => {
          e.preventDefault();
          onMessage();
        }}
        style={sx('display:inline-flex;align-items:center;min-height:44px;padding:0 18px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;')}
      >
        Message the team
      </a>
    </div>
  );
}

export function FpActivityRow({
  residentId,
  first,
  moments,
  weekly,
  onWeekly,
  readOnly,
  readOnlyLine,
}: {
  residentId: string;
  first: string;
  moments: FamilyMomentsResponse | null | undefined;
  weekly: boolean;
  onWeekly: (on: boolean) => void;
  readOnly: boolean;
  readOnlyLine: string;
}) {
  const { client, session } = useSession();
  const ch = useQuery((signal) => familyAlertChannels(client, { params: { residentId }, signal }), [client, session?.token, residentId]);
  const [pick, setPick] = useState<CheckInAnswer | null>(null);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState<CheckInAnswer | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const send = async () => {
    if (!pick || readOnly || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const call = wantsCallBack(pick);
      const n = note.trim();
      const extra = n ? ' ' + n + (/[.!?]$/.test(n) ? '' : '.') : '';
      await familyRequest(client, { params: { residentId }, body: { kind: call ? 'checkin:callback' : 'checkin', text: `Weekly check-in for ${first}: ${pick}.${extra}${call ? ' Please call me back.' : ''}` } });
      setSent(pick);
    } catch (e) {
      setErr((e as ApiError)?.message ?? 'The answer did not send. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-screen-label="Family activity and check-in" data-testid="fp-activity-row" style={sx('grid-column:1/-1;display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,320px),1fr));gap:10px;min-width:0;')}>
      <div data-testid="fp-feed" style={sx(CARD + 'gap:2px;')}>
        <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;margin-bottom:4px;')}>
          <span style={sx(LABEL)}>{`Today with ${first}`}</span>
        </div>
        <span data-testid="fp-feed-empty" style={sx('font-size:16px;color:#22272E;line-height:1.4;text-wrap:pretty;padding:7px 0;border-top:1px solid rgba(34,39,46,.08);')}>
          Not connected yet. What the team logs during the day shows here once it is connected.
        </span>
        <span style={sx('font-size:14px;color:#54606C;line-height:1.4;margin-top:4px;')}>Only what the team logged. Medical details stay in the care plan and doctor visits.</span>
      </div>

      <div data-screen-label="Family quick check-in" data-testid="fp-checkin" style={sx(CARD)}>
        <span style={sx(LABEL)}>Quick check-in</span>
        {sent ? (
          <span data-testid="fp-checkin-thanks" style={sx('font-size:16px;color:#22272E;line-height:1.45;')}>{checkInThanks(sent)}</span>
        ) : (
          <>
            <span style={sx('font-size:17px;font-weight:700;line-height:1.35;')}>{`How did this week go for ${first}?`}</span>
            {readOnly ? (
              <span data-testid="fp-checkin-ro" style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{readOnlyLine}</span>
            ) : (
              <>
                <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
                  {CHECKIN_OPTIONS.map((o) => (
                    <a key={o} href="#" role="button" aria-pressed={pick === o} data-testid={'fp-checkin-' + o} onClick={(e) => { e.preventDefault(); setPick(o); }} style={sx(seg(pick === o))}>
                      {o}
                    </a>
                  ))}
                </div>
                {pick && (
                  <>
                    <textarea aria-label="Anything to add" placeholder="Anything to add? (optional)" value={note} onChange={(e) => setNote(e.target.value)} style={sx('min-height:64px;padding:10px 12px;border-radius:10px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;color:#22272E;font:inherit;font-size:16px;resize:vertical;')} />
                    <a href="#" data-testid="fp-checkin-send" aria-disabled={busy || undefined} onClick={(e) => { e.preventDefault(); void send(); }} style={sx('display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:6px 12px;box-sizing:border-box;border-radius:8px;background:#2F5C97;color:#fff;font-size:16px;font-weight:700;text-decoration:none;line-height:1.25;white-space:nowrap;align-self:flex-start;' + (busy ? 'opacity:.6;' : ''))}>
                      Send
                    </a>
                  </>
                )}
              </>
            )}
            <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>One question a week. The care team reads every answer.</span>
          </>
        )}
        {err && <span role="alert" style={sx('font-size:16px;font-weight:700;color:#B3261E;line-height:1.4;')}>{err}</span>}
      </div>

      <div data-screen-label="Family updates settings" data-testid="fp-prefs" style={sx(CARD)}>
        <span style={sx(LABEL)}>How you hear from us</span>
        <div style={sx('display:flex;flex-direction:column;gap:5px;padding:6px 0;border-top:1px solid rgba(34,39,46,.08);')}>
          <span style={sx('font-size:16px;font-weight:700;')}>Reach me by</span>
          <span data-testid="fp-prefs-reach" style={sx('font-size:16px;color:#22272E;')}>{ch.data ? channelWords(ch.data) : ch.error ? 'Not connected yet' : ''}</span>
          {ch.data?.always && <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{ch.data.always}</span>}
          <Link to="/settings?section=my-connections" style={sx('font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;align-self:flex-start;')}>
            Change in Settings
          </Link>
        </div>
        <div style={sx('display:flex;flex-direction:column;gap:5px;padding:6px 0;border-top:1px solid rgba(34,39,46,.08);')}>
          <span style={sx('font-size:16px;font-weight:700;')}>Weekly summary</span>
          {readOnly ? (
            <span style={sx('font-size:16px;color:#22272E;')}>{weekly ? 'Every Sunday' : 'No thanks'}</span>
          ) : (
            <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
              <a href="#" role="button" aria-pressed={weekly} data-testid="fp-prefs-weekly-on" onClick={(e) => { e.preventDefault(); if (!weekly) onWeekly(true); }} style={sx(seg(weekly))}>
                Every Sunday
              </a>
              <a href="#" role="button" aria-pressed={!weekly} data-testid="fp-prefs-weekly-off" onClick={(e) => { e.preventDefault(); if (weekly) onWeekly(false); }} style={sx(seg(!weekly))}>
                No thanks
              </a>
            </div>
          )}
          <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{weekly ? 'One short email each Sunday.' : 'No weekly email. Everything stays here in the portal.'}</span>
        </div>
        <div style={sx('display:flex;flex-direction:column;gap:5px;padding:6px 0;border-top:1px solid rgba(34,39,46,.08);')}>
          <span style={sx('font-size:16px;font-weight:700;')}>Photos from activities</span>
          <span data-testid="fp-prefs-photos" style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{photoWords(moments?.photoConsent, first)}</span>
        </div>
      </div>
    </div>
  );
}
